/**
 * Flow map auto layout: a small, deterministic Sugiyama pass.
 *
 * 1. Blocks: every top-level node is one block; a group is one block that
 *    carries its children stacked inside it (and is resized to fit them).
 * 2. Arrows between blocks (an arrow touching a child counts for its group),
 *    with cycles broken by a depth-first walk in document order.
 * 3. Layers by longest path from a root, ordered inside by one down and one up
 *    barycentre sweep (ties by document order).
 * 4. Bands along the main axis, items centred on the cross axis.
 *
 * Nothing here reads a node's current position except the order of children
 * inside a group, and that order is reproduced by its own output, so running
 * the layout twice returns the SAME object the second time.
 */
import {
  FLOW_DOC_VERSION,
  FLOW_LIMITS,
  type FlowDirection,
  type FlowEdge,
  type FlowMapDoc,
  type FlowNode,
  type FlowPoint,
  type FlowSize,
} from "../model.js";
import { clampCoord, nodeSize, round1 } from "./geometry.js";
import { kahn } from "./readingOrder.js";

export interface AutoLayoutOptions {
  direction?: FlowDirection;
  gapX?: number;
  gapY?: number;
}

const GROUP_PAD = 24;
const GROUP_HEAD = 40;
const GROUP_GAP = 16;
const GROUP_MIN_WIDTH = 280;

interface Block {
  node: FlowNode;
  children: FlowNode[];
  size: FlowSize;
  /** Children's positions relative to the group. */
  inner: Map<string, FlowPoint>;
}

const gapOf = (v: number | undefined, fallback: number): number => (typeof v === "number" && Number.isFinite(v) ? Math.max(0, v) : fallback);

const clampSize = (n: number): number => Math.round(Math.max(FLOW_LIMITS.sizeMin, Math.min(FLOW_LIMITS.sizeMax, n)));

/**
 * Children of one group in reading order, from the arrows between them and
 * then their current place (box centres, main axis first). Idempotent on its
 * own output: `packGroup` puts the children on a grid whose centres sort back
 * into exactly this order, so a second run keeps it.
 */
function orderChildren(children: FlowNode[], doc: FlowMapDoc, lr: boolean): FlowNode[] {
  const index = new Map(children.map((n, i) => [n.id, i] as const));
  const byId = new Map(children.map((n) => [n.id, n] as const));
  const centre = new Map(
    children.map((n) => {
      const s = nodeSize(n);
      return [n.id, { x: n.position.x + s.width / 2, y: n.position.y + s.height / 2 }] as const;
    }),
  );
  const before = (a: string, b: string): number => {
    const pa = centre.get(a)!;
    const pb = centre.get(b)!;
    const main = lr ? pa.x - pb.x : pa.y - pb.y;
    if (main) return main;
    const cross = lr ? pa.y - pb.y : pa.x - pb.x;
    return cross || index.get(a)! - index.get(b)!;
  };
  const ids = kahn(
    children.map((n) => n.id),
    doc.edges.map((e) => [e.source, e.target] as const),
    before,
  );
  return ids.map((id) => byId.get(id)!);
}

/** Sum of `sizes` with a gap between neighbours. */
const spanOf = (sizes: number[]): number => sizes.reduce((s, v) => s + v, 0) + GROUP_GAP * Math.max(0, sizes.length - 1);

/** Where each of `sizes` starts when laid end to end from `start` with gaps. */
function starts(sizes: number[], start: number): number[] {
  const out: number[] = [];
  let at = start;
  for (const s of sizes) {
    out.push(at);
    at += s + GROUP_GAP;
  }
  return out;
}

/**
 * Stack a group's children under a header band, in one column (TB) or one row
 * (LR), and size the group to fit. When one lane would push the group past the
 * maximum box size, the children go on a grid instead: `lanes` of them side by
 * side per step, still read in order (across, then on). Every child is centred
 * in its cell, so children sharing a step share a centre line.
 */
function packGroup(block: Block, lr: boolean): void {
  const kids = block.children;
  const n = kids.length;
  if (!n) return;
  const sizes = kids.map((k) => nodeSize(k));
  const mainOf = (s: FlowSize): number => (lr ? s.width : s.height);
  const crossOf = (s: FlowSize): number => (lr ? s.height : s.width);
  // The header band sits on top: along the main axis in TB, across it in LR.
  const mainRoom = FLOW_LIMITS.sizeMax - (lr ? 2 * GROUP_PAD : GROUP_HEAD + GROUP_PAD);
  const measure = (lanes: number): { bands: number[]; cols: number[] } => {
    const bands = new Array<number>(Math.ceil(n / lanes)).fill(0);
    const cols = new Array<number>(Math.min(lanes, n)).fill(0);
    sizes.forEach((s, i) => {
      const r = Math.floor(i / lanes);
      const c = i % lanes;
      bands[r] = Math.max(bands[r]!, mainOf(s));
      cols[c] = Math.max(cols[c]!, crossOf(s));
    });
    return { bands, cols };
  };
  // The fewest lanes whose steps fit (more lanes only widen the group).
  let lanes = 1;
  let grid = measure(lanes);
  while (lanes < n && spanOf(grid.bands) > mainRoom) grid = measure(++lanes);

  const mainSpan = spanOf(grid.bands);
  const crossSpan = spanOf(grid.cols);
  const width = Math.max(GROUP_MIN_WIDTH, (lr ? mainSpan : crossSpan) + 2 * GROUP_PAD);
  const height = GROUP_HEAD + (lr ? crossSpan : mainSpan) + GROUP_PAD;
  // Content is centred across whatever extra width the minimum group width adds.
  const bandAt = starts(grid.bands, lr ? (width - mainSpan) / 2 : GROUP_HEAD);
  const laneAt = starts(grid.cols, lr ? GROUP_HEAD : (width - crossSpan) / 2);
  sizes.forEach((s, i) => {
    const r = Math.floor(i / lanes);
    const c = i % lanes;
    const m = bandAt[r]! + (grid.bands[r]! - mainOf(s)) / 2;
    const x = laneAt[c]! + (grid.cols[c]! - crossOf(s)) / 2;
    block.inner.set(kids[i]!.id, lr ? { x: round1(m), y: round1(x) } : { x: round1(x), y: round1(m) });
  });
  block.size = { width: clampSize(width), height: clampSize(height) };
}

/** Drop the arrows that close a cycle, found by a depth-first walk in block order. */
function acyclic(succ: number[][]): number[][] {
  const n = succ.length;
  const state = new Uint8Array(n); // 0 unseen, 1 on the current path, 2 finished
  const dag: number[][] = succ.map(() => []);
  for (let root = 0; root < n; root++) {
    if (state[root]) continue;
    state[root] = 1;
    const stack: Array<{ v: number; i: number }> = [{ v: root, i: 0 }];
    while (stack.length) {
      const top = stack[stack.length - 1]!;
      const next = succ[top.v]!;
      if (top.i < next.length) {
        const w = next[top.i++]!;
        if (state[w] === 1) continue; // a back arrow: ignored for layering
        dag[top.v]!.push(w);
        if (state[w] === 0) {
          state[w] = 1;
          stack.push({ v: w, i: 0 });
        }
      } else {
        state[top.v] = 2;
        stack.pop();
      }
    }
  }
  return dag;
}

/** Layer = the longest path from a root. */
function longestPathLayers(dag: number[][]): number[] {
  const n = dag.length;
  const indeg = new Array<number>(n).fill(0);
  for (const out of dag) for (const w of out) indeg[w]!++;
  const layer = new Array<number>(n).fill(0);
  const queue: number[] = [];
  for (let v = 0; v < n; v++) if (indeg[v] === 0) queue.push(v);
  for (let qi = 0; qi < queue.length; qi++) {
    const v = queue[qi]!;
    for (const w of dag[v]!) {
      layer[w] = Math.max(layer[w]!, layer[v]! + 1);
      if (--indeg[w]! === 0) queue.push(w);
    }
  }
  return layer;
}

/** One barycentre sweep. `link` gives each block's neighbours in the reference layers. */
function sweep(layers: number[][], order: number[], pos: Float64Array, link: number[][], down: boolean): void {
  const count = layers.length;
  for (let step = 1; step < count; step++) {
    const li = down ? step : count - 1 - step;
    const layer = layers[li]!;
    const bc = new Map<number, number>();
    for (const v of layer) {
      const near = link[v]!;
      bc.set(v, near.length ? near.reduce((s, u) => s + pos[u]!, 0) / near.length : pos[v]!);
    }
    layer.sort((a, b) => bc.get(a)! - bc.get(b)! || order[a]! - order[b]!);
    layer.forEach((v, i) => (pos[v] = i - (layer.length - 1) / 2));
  }
}

/**
 * Lay the map out along its arrows. Groups move as one block with their
 * children stacked inside; arrows touching a child count as arrows of its
 * group. Sets `direction` (option, else the map's, else TB), drops every
 * explicit handle so the direction's defaults apply, and returns the same
 * object when nothing moves.
 */
export function autoLayout(doc: FlowMapDoc, opts: AutoLayoutOptions = {}): FlowMapDoc {
  const direction: FlowDirection = opts.direction ?? doc.direction ?? "TB";
  const lr = direction === "LR";
  const gapX = gapOf(opts.gapX, 48);
  const gapY = gapOf(opts.gapY, 72);

  // 1. Blocks: every top-level node; a group carries its children.
  const kindById = new Map(doc.nodes.map((n) => [n.id, n.kind] as const));
  const groupOf = (n: FlowNode): string | null =>
    n.kind !== "group" && n.parentId && kindById.get(n.parentId) === "group" ? n.parentId : null;
  const blocks: Block[] = [];
  const blockOf = new Map<string, number>();
  for (const n of doc.nodes) {
    if (groupOf(n)) continue;
    blockOf.set(n.id, blocks.length);
    blocks.push({ node: n, children: [], size: nodeSize(n), inner: new Map() });
  }
  for (const n of doc.nodes) {
    const g = groupOf(n);
    if (!g) continue;
    const b = blockOf.get(g)!;
    blockOf.set(n.id, b);
    blocks[b]!.children.push(n);
  }
  for (const b of blocks) {
    if (b.node.kind !== "group") continue;
    b.children = orderChildren(b.children, doc, lr);
    packGroup(b, lr);
  }

  // 2. Arrows between blocks, deduplicated, successors in block order.
  const succSets = blocks.map(() => new Set<number>());
  for (const e of doc.edges) {
    const a = blockOf.get(e.source);
    const b = blockOf.get(e.target);
    if (a === undefined || b === undefined || a === b) continue;
    succSets[a]!.add(b);
  }
  const succ = succSets.map((s) => [...s].sort((x, y) => x - y));
  const dag = acyclic(succ);
  const layerOf = longestPathLayers(dag);

  // 3. Layers in block order, then one down and one up barycentre sweep.
  const layers: number[][] = [];
  blocks.forEach((_, v) => (layers[layerOf[v]!] ??= []).push(v));
  for (let i = 0; i < layers.length; i++) layers[i] ??= [];
  const order = blocks.map((_, v) => v);
  const pos = new Float64Array(blocks.length);
  for (const layer of layers) layer.forEach((v, i) => (pos[v] = i - (layer.length - 1) / 2));
  const preds: number[][] = blocks.map(() => []);
  dag.forEach((out, v) => out.forEach((w) => preds[w]!.push(v)));
  sweep(layers, order, pos, preds, true);
  sweep(layers, order, pos, dag, false);

  // 4. Place: bands along the main axis, items centred on the cross axis.
  const placed = new Array<FlowPoint>(blocks.length);
  const mainGap = lr ? gapX : gapY;
  const crossGap = lr ? gapY : gapX;
  let cursor = 0;
  for (const layer of layers) {
    if (!layer.length) continue;
    const band = layer.reduce((m, v) => Math.max(m, lr ? blocks[v]!.size.width : blocks[v]!.size.height), 0);
    const span = layer.reduce((sum, v, i) => sum + (i ? crossGap : 0) + (lr ? blocks[v]!.size.height : blocks[v]!.size.width), 0);
    let c = -span / 2;
    for (const v of layer) {
      const s = blocks[v]!.size;
      const along = lr ? s.width : s.height;
      const across = lr ? s.height : s.width;
      const m = cursor + (band - along) / 2;
      placed[v] = lr ? { x: m, y: c } : { x: c, y: m };
      c += across + crossGap;
    }
    cursor += band + mainGap;
  }
  // A very long chain would leave the coordinate range: centre it on the origin instead.
  const extent = cursor - mainGap;
  const shift = extent > FLOW_LIMITS.coord ? -extent / 2 : 0;

  // 5. Write back, reusing every node object that did not change.
  let changed = doc.direction !== direction;
  const nodes = doc.nodes.map((n) => {
    const b = blockOf.get(n.id);
    if (b === undefined) return n;
    const block = blocks[b]!;
    let position: FlowPoint;
    if (block.node === n) {
      const p = placed[b]!;
      position = { x: clampCoord(lr ? p.x + shift : p.x), y: clampCoord(lr ? p.y : p.y + shift) };
    } else {
      position = block.inner.get(n.id) ?? n.position;
    }
    const size = n.kind === "group" && block.node === n && block.children.length ? block.size : n.size;
    const moved = position.x !== n.position.x || position.y !== n.position.y;
    const resized = n.kind === "group" && (size?.width !== n.size?.width || size?.height !== n.size?.height);
    if (!moved && !resized) return n;
    changed = true;
    const next: FlowNode = { ...n, position };
    if (resized && size) next.size = { width: size.width, height: size.height };
    return next;
  });
  const edges = doc.edges.map((e) => {
    if (e.sourceHandle === undefined && e.targetHandle === undefined) return e;
    changed = true;
    const next: FlowEdge = { ...e };
    delete next.sourceHandle;
    delete next.targetHandle;
    return next;
  });
  if (!changed) return doc;
  return {
    version: FLOW_DOC_VERSION,
    direction,
    ...(doc.edgeStyle !== undefined ? { edgeStyle: doc.edgeStyle } : {}),
    nodes,
    edges,
  };
}
